/**
 * 报销单详情（延伸需求 3.2 / 3.3 / 3.5）
 *
 * 一张报销单的完整面貌：明细、发票与校验结果、付款、审批留痕，以及在这页能做的事
 * （上传/释放发票、提交审批、付款登记）。
 *
 * 【发票校验结果为什么占这么大篇幅】报销的风险点几乎全在发票上——
 * 抬头、金额一致性、品类是否对得上、有没有被别的单用过。这些结论要一眼看得到，
 * 而不是点进另一页再回来。
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Timeline,
} from 'antd';
import { ArrowLeftOutlined, CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ExpensePaymentMethod,
  EXPENSE_PAYMENT_METHOD_LABELS,
  ExpenseReimburseStatus,
  EXPENSE_REIMBURSE_STATUS_LABELS,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  PermissionCode,
  RiskLevel,
  RISK_ITEM_LABELS,
  type RiskItem,
} from '@ilc/shared';
import { expenseApi } from '@/api/endpoints';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { Money, moneyCellStyle } from '@/components/Money';
import { MaskedField } from '@/components/MaskedField';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { FileUploader } from '@/components/FileUploader';
import { RiskTag } from '@/components/RiskTag';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

export function ExpenseReimburseDetailPage(): React.ReactElement {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);

  const res = useResource(() => expenseApi.reimburseDetail(id), { deps: [id], enabled: !!id });

  const canReimburse = can(PermissionCode.EXPENSE_REIMBURSE);
  const canPay = can(PermissionCode.EXPENSE_PAY);

  const [submitTarget, setSubmitTarget] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<{ id: string; no: string } | null>(null);
  const [payModal, setPayModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payForm] = Form.useForm();

  if (res.loading || res.error || !res.data) {
    return (
      <div className="ilc-page">
        <PageHeader title="报销单详情" />
        <PageState
          loading={res.loading}
          error={res.error}
          empty={!res.loading && !res.error && !res.data}
          emptyText="报销单不存在或不在你的可见范围内"
          onRetry={res.reload}
        >
          <div />
        </PageState>
      </div>
    );
  }

  const r = res.data;
  const isDraft = r.status === ExpenseReimburseStatus.DRAFT;
  const isRejected = r.status === ExpenseReimburseStatus.REJECTED;
  const canEdit = canReimburse && (isDraft || isRejected);
  const riskItems = (r.riskItems ?? []) as RiskItem[];

  const doSubmit = async (note: string): Promise<void> => {
    const result = await performAction({
      action: () => expenseApi.submitReimburse(id),
      successMessage: '已提交审批',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次提交仅在本地演示',
    });
    if (result.ok) {
      const warning = (result.data as { budgetWarning?: string | null } | null)?.budgetWarning;
      /**
       * 预算提示**无论成败都要显示**：默认策略下超预算仍可提交，
       * 所以它不是错误，而是"你知道这件事吗"。
       */
      if (warning) feedback.warning(warning);
      if (note) feedback.info(`提交说明：${note}`);
      setSubmitTarget(false);
      res.reload();
    }
  };

  const doRelease = async (reason: string): Promise<void> => {
    if (!releaseTarget) return;
    const result = await performAction({
      action: () => expenseApi.releaseInvoice(id, releaseTarget.id, reason),
      successMessage: '发票已释放，可重新用于其它报销单',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次释放仅在本地演示',
    });
    if (result.ok) {
      setReleaseTarget(null);
      res.reload();
    }
  };

  const doPay = async (): Promise<void> => {
    const v = await payForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          expenseApi.pay(id, {
            amountCents: v.amountYuan ? Math.round(Number(v.amountYuan) * 100) : undefined,
            method: v.method,
            payDate: v.payDate ? dayjs(v.payDate).format('YYYY-MM-DD') : null,
            remark: v.remark || null,
          }),
        successMessage: '付款已登记',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次登记仅在本地演示',
      });
      if (result.ok) {
        setPayModal(false);
        payForm.resetFields();
        res.reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const paidCents = (r.payments ?? []).reduce((sum, p) => sum + p.amountCents, 0);
  const payProgress = r.amountCents > 0 ? Math.min(100, (paidCents / r.amountCents) * 100) : 0;
  const exec = r.budgetExecution;

  return (
    <div className="ilc-page">
      <PageHeader
        title={
          <Space>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/business/expenses/reimburse')}>
              返回列表
            </Button>
            <span>
              {r.reimburseNo} {r.reason}
            </span>
          </Space>
        }
        tags={
          <Space wrap>
            <Tag color="blue">{EXPENSE_TYPE_LABELS[r.expenseType as ExpenseType] ?? r.expenseType}</Tag>
            <Tag
              color={
                r.status === ExpenseReimburseStatus.APPROVED || r.status === ExpenseReimburseStatus.PAID
                  ? 'success'
                  : r.status === ExpenseReimburseStatus.REJECTED
                    ? 'warning'
                    : 'processing'
              }
            >
              {EXPENSE_REIMBURSE_STATUS_LABELS[r.status as ExpenseReimburseStatus] ?? r.status}
            </Tag>
            {r.riskLevel !== RiskLevel.NONE && <RiskTag level={r.riskLevel} />}
            {r.rejectCount > 0 && <Tag color="warning">已退回 {r.rejectCount} 次</Tag>}
          </Space>
        }
        description={
          <span>
            申请人：{r.applicantName ?? '-'}　部门：{r.deptName ?? '-'}
            {r.projectName && <>　工程：{r.projectName}</>}
            {r.contractNo && <>　合同：{r.contractNo}</>}
            {r.applyNo && <>　事前申请：{r.applyNo}</>}
          </span>
        }
        extra={
          <Space wrap>
            {canEdit && (
              <Button onClick={() => navigate(`/business/expenses/reimburse/${id}/edit`)}>编辑明细</Button>
            )}
            {canReimburse && (isDraft || isRejected) && (
              <Button type="primary" onClick={() => setSubmitTarget(true)}>
                提交审批
              </Button>
            )}
            {canPay && (r.status === ExpenseReimburseStatus.APPROVED || r.status === ExpenseReimburseStatus.PAID) && (
              <Button type="primary" onClick={() => setPayModal(true)}>
                登记付款
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={res.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      {r.lastRejectReason && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="本单被退回"
          description={`退回原因：${r.lastRejectReason}。修改后重新提交即可。`}
        />
      )}

      {riskItems.length > 0 && (
        <Alert
          type={r.riskLevel === RiskLevel.HIGH ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
          message={`校验发现 ${riskItems.length} 项需要关注（风险等级：${r.riskLevel}）`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {riskItems.map((it, i) => (
                <li key={`${it.code}-${i}`}>
                  <Tag>{RISK_ITEM_LABELS[it.code] ?? it.code}</Tag>
                  {it.message}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="报销金额" value={r.amountCents / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="明细条数" value={r.itemCount ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="关联发票" value={r.invoiceCount ?? 0} suffix="张" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="已付款" value={paidCents / 100} precision={2} prefix="¥" />
            {r.amountCents > 0 && <Progress percent={Math.round(payProgress)} size="small" />}
          </Card>
        </Col>
      </Row>

      {exec && (
        <Card size="small" title="预算执行" style={{ marginBottom: 12 }}>
          <Descriptions size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="当前预算">
              <Money cents={exec.currentCents} />
            </Descriptions.Item>
            <Descriptions.Item label="已用">
              <Money cents={exec.usedCents} />
            </Descriptions.Item>
            <Descriptions.Item label="在途">
              <Money cents={exec.pendingCents} />
            </Descriptions.Item>
          </Descriptions>
          {exec.usedRatio !== null && (
            <Progress
              percent={Math.min(100, Math.round(exec.usedRatio * 100))}
              status={exec.usedRatio > 1 ? 'exception' : exec.usedRatio >= 0.8 ? 'active' : 'normal'}
              format={(p) => `${p}%`}
            />
          )}
        </Card>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={14}>
          {/* —— 明细 —— */}
          <Card size="small" title={`费用明细（${(r.items ?? []).length}）`} style={{ marginBottom: 12 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={r.items ?? []}
              locale={{ emptyText: <Empty description="还没有明细" /> }}
              columns={[
                { title: '日期', dataIndex: 'itemDate', width: 100 },
                { title: '事项', dataIndex: 'subject', ellipsis: true },
                {
                  title: '金额',
                  dataIndex: 'amountCents',
                  align: 'right' as const,
                  onCell: () => ({ style: moneyCellStyle }),
                  render: (v: number) => <Money cents={v} />,
                },
                {
                  title: '发票',
                  dataIndex: 'invoiceNo',
                  width: 170,
                  render: (v: string | null, row: { invoiceId: string | null }) =>
                    v ? <span className="ilc-mono">{v}</span> : <Tag>未关联发票</Tag>,
                },
                {
                  title: '金额一致',
                  dataIndex: 'invoiceAmountMatched',
                  width: 90,
                  // null = 没有发票可比（与 false「有票但金额不符」是两回事）
                  render: (v: boolean | null) =>
                    v === null ? (
                      <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
                    ) : v ? (
                      <Tag color="success">一致</Tag>
                    ) : (
                      <Tag color="warning">不一致</Tag>
                    ),
                },
              ]}
            />
          </Card>

          {/* —— 发票 —— */}
          <Card
            size="small"
            title={`关联发票（${(r.invoices ?? []).length}）`}
            style={{ marginBottom: 12 }}
            extra={
              canEdit ? (
                <FileUploader
                  compact
                  accept=".pdf,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.webp"
                  title="上传发票"
                  description="上传后会自动识别、验真并查重（几十秒）"
                  upload={async (file) => expenseApi.uploadInvoice(id, file)}
                  onUploaded={() => {
                    feedback.success('发票已上传并完成校验');
                    res.reload();
                  }}
                />
              ) : null
            }
          >
            {(r.invoices ?? []).length === 0 ? (
              <Empty
                image={<CloudUploadOutlined style={{ fontSize: 40, color: 'rgba(0,0,0,0.25)' }} />}
                description={
                  canEdit
                    ? '还没有关联发票。点右上角「上传发票」——上传时会自动完成 OCR、验真与重复报销查重。'
                    : '还没有关联发票'
                }
              />
            ) : (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={r.invoices ?? []}
                columns={[
                  {
                    title: '发票号码',
                    dataIndex: 'invoiceNo',
                    width: 180,
                    render: (v: string | null) => <span className="ilc-mono">{v ?? '-'}</span>,
                  },
                  { title: '开票日期', dataIndex: 'invoiceDate', width: 100, render: (v: string | null) => v ?? '-' },
                  { title: '销方', dataIndex: 'sellerName', ellipsis: true, render: (v: string | null) => v ?? '-' },
                  {
                    title: '价税合计',
                    dataIndex: 'totalCents',
                    align: 'right' as const,
                    onCell: () => ({ style: moneyCellStyle }),
                    render: (v: number | null) => <Money cents={v} />,
                  },
                  {
                    title: '金额一致',
                    dataIndex: 'amountMatched',
                    width: 90,
                    render: (v: boolean) =>
                      v ? <Tag color="success">一致</Tag> : <Tag color="warning">不一致</Tag>,
                  },
                  {
                    title: '风险',
                    dataIndex: 'riskLevel',
                    width: 80,
                    render: (v: string | null) =>
                      v && v !== RiskLevel.NONE ? <RiskTag level={v as RiskLevel} /> : <Tag>无</Tag>,
                  },
                  {
                    title: '发票状态',
                    dataIndex: 'invoiceStatus',
                    width: 100,
                    render: (v: string | null) =>
                      v ? (INVOICE_STATUS_LABELS[v as keyof typeof INVOICE_STATUS_LABELS] ?? v) : '-',
                  },
                  {
                    title: '操作',
                    key: 'act',
                    width: 80,
                    render: (_: unknown, row: { id: string; invoiceNo: string | null }) =>
                      canEdit ? (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setReleaseTarget({ id: row.id, no: row.invoiceNo ?? row.id })}
                        >
                          释放
                        </Button>
                      ) : null,
                  },
                ]}
              />
            )}

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="「释放」不是删除：这张票会解除与本单的关联，可以重新用于别的报销单"
              description="释放记录会留痕（时间与理由），事后查得出一票多报的历史。退回或撤销报销单时系统会自动释放它名下的发票。"
            />
          </Card>

          {/* —— 付款 —— */}
          <Card size="small" title={`付款记录（${(r.payments ?? []).length}）`}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={r.payments ?? []}
              locale={{ emptyText: <Empty description="还没有付款记录" /> }}
              columns={[
                { title: '付款单号', dataIndex: 'paymentNo', render: (v: string) => <span className="ilc-mono">{v}</span> },
                {
                  title: '金额',
                  dataIndex: 'amountCents',
                  align: 'right' as const,
                  onCell: () => ({ style: moneyCellStyle }),
                  render: (v: number) => <Money cents={v} strong />,
                },
                {
                  title: '方式',
                  dataIndex: 'method',
                  width: 100,
                  render: (v: string) => EXPENSE_PAYMENT_METHOD_LABELS[v as ExpensePaymentMethod] ?? v,
                },
                { title: '付款日期', dataIndex: 'payDate', width: 110, render: (v: string | null) => v ?? '-' },
                { title: '经办', dataIndex: 'paidByName', width: 90, render: (v: string | null) => v ?? '-' },
                {
                  title: 'T+ 凭证号',
                  dataIndex: 'voucherNo',
                  width: 140,
                  /**
                   * 凭证号**只能由 T+ 回调写入**（系统绝不自动生成记账凭证）。
                   * 空表示财务还没在 T+ 端选科目——这是正常状态，不是"同步失败"。
                   */
                  render: (v: string | null) =>
                    v ? (
                      <span className="ilc-mono">{v}</span>
                    ) : (
                      <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                        待财务在 T+ 选科目
                      </span>
                    ),
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          {/* —— 基本信息 —— */}
          <Card size="small" title="基本信息" style={{ marginBottom: 12 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="收款人">{r.payeeName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="收款账号">
                <MaskedField value={r.payeeAccount} kind="bankAccount" permission={PermissionCode.EXPENSE_PAY} />
              </Descriptions.Item>
              <Descriptions.Item label="开户行">{r.payeeBank ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="提交时间">
                {r.submittedAt ? dayjs(r.submittedAt).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="通过时间">
                {r.approvedAt ? dayjs(r.approvedAt).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="付清时间">
                {r.paidAt ? dayjs(r.paidAt).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="归档时间">
                {r.archivedAt ? dayjs(r.archivedAt).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* —— 审批留痕 —— */}
          <Card size="small" title="审批留痕">
            {(r.approvals ?? []).length > 0 ? (
              <Timeline
                items={(r.approvals ?? []).map((a) => ({
                  color: a.status === 'APPROVED' ? 'green' : a.status === 'REJECTED' ? 'red' : 'blue',
                  children: (
                    <div>
                      <Space size={4} wrap>
                        <strong>{a.nodeName}</strong>
                        <Tag>{a.assigneeRoleName ?? '—'}</Tag>
                        {a.assigneeUserName && <span>{a.assigneeUserName}</span>}
                      </Space>
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                        {dayjs(a.handledAt ?? a.createdAt).format('YYYY-MM-DD HH:mm')}
                      </div>
                      {a.comment && <div style={{ marginTop: 4 }}>{a.comment}</div>}
                    </div>
                  ),
                }))}
              />
            ) : (
              <List
                size="small"
                dataSource={[]}
                locale={{ emptyText: <Empty description="尚未提交审批" /> }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* —— 提交审批 —— */}
      <ConfirmDanger
        open={submitTarget}
        title={`提交审批：${r.reimburseNo}`}
        description={
          <span>
            金额 <strong>¥{(r.amountCents / 100).toFixed(2)}</strong>，明细 {r.itemCount ?? 0} 条，
            发票 {r.invoiceCount ?? 0} 张。
            <br />
            提交后进入审批链（部门负责人 → 财务初审 → 复核 / 领导，按金额分流）→ 出纳付款 → 归档。
            被退回的发票会自动释放，可以换票重报。
          </span>
        }
        level="normal"
        reasonLabel="提交说明（选填）"
        minReasonLength={0}
        confirmText="确认提交"
        onConfirm={(note) => void doSubmit(note)}
        onCancel={() => setSubmitTarget(false)}
      />

      {/* —— 释放发票 —— */}
      <ConfirmDanger
        open={releaseTarget !== null}
        title={`释放发票：${releaseTarget?.no ?? ''}`}
        description="释放后这张票可以重新用于别的报销单，本单也会重算校验（那条「重复报销」的风险随之消失）。释放记录会留痕。"
        level="high"
        reasonLabel="释放理由（必填，将写入操作日志）"
        minReasonLength={4}
        reasonPresets={['票面有误，需换票重报', '误传，应属于其它报销单', '金额不符，退回重填']}
        confirmText="确认释放"
        onConfirm={(reason) => void doRelease(reason)}
        onCancel={() => setReleaseTarget(null)}
      />

      {/* —— 登记付款 —— */}
      <Modal
        open={payModal}
        title="登记付款"
        onCancel={() => setPayModal(false)}
        onOk={() => void doPay()}
        confirmLoading={saving}
        destroyOnHidden
        okText="登记"
        width={560}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`报销金额 ¥${(r.amountCents / 100).toFixed(2)}，已付 ¥${(paidCents / 100).toFixed(2)}`}
          description="留空金额表示付清剩余部分。付款执行目前由出纳在网银完成后再回来登记——对接银行通道属二期。"
        />
        <Form
          key="pay"
          form={payForm}
          layout="vertical"
          preserve={false}
          initialValues={{ method: ExpensePaymentMethod.BANK }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amountYuan" label="付款金额（元）" extra="留空=付清剩余">
                <InputNumber style={{ width: '100%' }} precision={2} min={0.01} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="method" label="付款方式" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(EXPENSE_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="payDate" label="付款日期">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ExpenseReimburseDetailPage;
